import { action, computed, observable } from 'mobx';

import { ViewController } from '@estee/elc-service';

import {
    BAD_CHAR_VALIDATOR_NAME,
    BARCODE_WIDTH_NARROW,
    BARCODE_WIDTH_WIDE,
    CodeType,
    UserIdentifier
} from '~constants/Constants';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { TranslationRepository } from '~repositories/TranslationRepository';
import { UserRepository } from '~repositories/UserRepository';
import { OnlineSignUpSubmittedUseCase } from '~use-cases/OnlineSignUpSubmittedUseCase';
import { FieldValidations } from '~utils/FieldValidations';
import { handlePlaceholderValues } from '~utils/FormatText';
import { createAsYouType } from '~utils/phone-number-helper';

interface IQRCodeViewController {
    userRepository: UserRepository;
    configRepository: ConfigRepository;
    translationRepository: TranslationRepository;
    messageRepository: MessageRepository;
}

export class QRCodeViewController extends ViewController {
    public userRepository: UserRepository;
    public configRepository: ConfigRepository;
    public translationRepository: TranslationRepository;
    private messageRepository: MessageRepository;
    @observable private mobileNumberText: string = '';

    constructor({
        userRepository,
        configRepository,
        translationRepository,
        messageRepository
    }: IQRCodeViewController) {
        super();
        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.translationRepository = translationRepository;
        this.messageRepository = messageRepository;
        this.formatMobileNumber();
    }

    @computed
    public get passwordValidations(): FieldValidations {
        const {
            rampCommon: { badCharValidation } = {},
            iamConfig: { fieldsValidations: { password: config } = {} } = {}
        } = this.configRepository;

        if (!config) {
            return new FieldValidations();
        }
        const { translations } = this.translationRepository;
        const { type, validations } = config;
        const fieldValidations = new FieldValidations({
            ...config,
            validations:
                type === 'string' && badCharValidation
                    ? (validations ?? []).concat(`${BAD_CHAR_VALIDATOR_NAME}:${badCharValidation}`)
                    : validations,
            errorMessages: {
                required: 'passwordRequiredMessage',
                default: 'passwordStrengthFailedResetPasswordError'
            },
            translations
        });

        return fieldValidations;
    }

    @action
    private formatMobileNumber = async () => {
        const {
            onlineSignUpFormStore: { values }
        } = this.userRepository;

        const mobileNumber = (values.mobileNumber ?? '') as string;
        const asYouType = createAsYouType();
        asYouType.input(mobileNumber);

        const phoneNumber = asYouType.getNumber();
        this.mobileNumberText = phoneNumber ? phoneNumber.formatNational() : mobileNumber;
    };

    // QR Code or barcode value
    private get userIdentifierCodeValue() {
        const { registeredUserState } = this.userRepository;
        const {
            userInfoDisplay: { userIdentifier: { identifier } = {} }
        } = this.configRepository;

        switch (identifier) {
            case UserIdentifier.USER_LOYALTY_NUMBER:
                return `${registeredUserState?.loyaltyMembershipNumber}`;
            case UserIdentifier.USER_LOYALTY_ID:
                return registeredUserState?.loyaltyGlobalIdentifier;
            default:
                return `${registeredUserState?.userId}`;
        }
    }

    @computed
    public get data() {
        const {
            isMobileOnlyRegistration,
            frontierErrorMessage,
            posIKalarCallbackUrl,
            onlineSignUpFormStore
        } = this.userRepository;
        const { values } = onlineSignUpFormStore;
        const {
            userInfoDisplay: {
                name,
                email,
                userIdentifier: { identifier, codeType = CodeType.QR_CODE } = {}
            },
            journey: { createOnlineAccount, hideScannableCode },
            iamConfig,
            socialRedirectUrl,
            dataConfig: { showLoginForm }
        } = this.configRepository;
        const codeValue = this.userIdentifierCodeValue;
        const showCode = codeValue && !hideScannableCode;
        const barCodeWidth =
            identifier === UserIdentifier.USER_LOYALTY_ID
                ? BARCODE_WIDTH_NARROW
                : BARCODE_WIDTH_WIDE;

        return {
            errorMessages: this.messageRepository.messages.get(MessageGroup.ONLINE_SIGN_UP),
            onlineSignUpFormStore,
            scannableCode: {
                codeValue,
                codeType,
                showCode,
                barCodeWidth
            },
            frontierErrorMessage,
            posIKalarCallbackUrl,
            passwordValidations: this.passwordValidations,
            createOnlineAccount,
            isMobileOnlyRegistration,
            userMobile: this.mobileNumberText,
            userName: handlePlaceholderValues(name, values, true),
            userEmail: handlePlaceholderValues(email, values, true),
            redirectButtonLabel: iamConfig?.redirectButtonLabel,
            socialRedirectUrl,
            showLoginForm
        };
    }

    @action
    public submitButtonClicked = async () => {
        const {
            onlineSignUpFormStore: { validateForm, isValid }
        } = this.userRepository;
        validateForm();
        if (isValid) {
            await new OnlineSignUpSubmittedUseCase().execute();
        }
    };
}
